// The conversation surface (#331 stage 1), built to design C — approved by
// Nick 2026-10-21. Mounted twice: full, as the EA home's right-hand side, and
// compact, inside the shell dock.
//
// ONE CONTINUOUS CONVERSATION
// ===========================
// Nick, 2026-10-19: one conversation with Donna, no list of separate chats.
// There is no thread switcher and no pinning here, because the backend models
// exactly one running transcript per owner and this shows that.
//
// THE NAME IS NEVER A LITERAL
// ===========================
// "Donna" is Nick's assistant, not every operator's. The name arrives as a
// prop from the owner's own setup; until they have chosen one it is "your
// assistant". There is no string "Donna" in this file, and a test asserts it.
//
// TWO THINGS IN THE DESIGN THAT NOTHING YET DRIVES
// ================================================
// The design shows one-tap answer buttons when the assistant offers a choice,
// and a caret while a reply appears. Both are built, and both are honest
// about what is behind them:
//
//   * Answer buttons render only when a turn actually carries `choices`.
//     Nothing populates that today, so none appear. They are not invented by
//     parsing her prose for question marks.
//   * The caret IS live generation now (#331, 2026-10-22). `streamingText`
//     holds the reply as it is produced and is rendered as a real turn; when
//     the stream ends it is dropped and the server's stored turn takes its
//     place, so what remains on screen is what was persisted and never what
//     this component accumulated. Where streaming is unavailable the plain
//     endpoint still answers and the brief reveal animation covers it.
//
// WHAT IT SAYS WHEN IT CANNOT HELP
// ================================
// Four honest states instead of one hopeful one: the subsystem is off, this
// owner's assistant is dormant, the read failed, or nothing has been said
// yet. Dormant is the common case today — every shell is `dormant` — and a
// composer that silently fails would be the worst of the four.
import React, { useEffect, useRef, useState } from "react";

import useConversation from "./useConversation";
import { MiniOrb } from "./DonnaOrb";

/** "Good morning" / "Good afternoon" / "Good evening", from the clock. */
export function greetingFor(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function OwnerTurn({ message }) {
  return (
    <li className="ea-turn ea-turn-owner">
      <div className="ea-bubble">{message.text}</div>
      {message.channel === "telegram" ? (
        <span className="ea-turn-channel">via Telegram</span>
      ) : null}
      {message.pending ? <span className="ea-turn-channel">sending…</span> : null}
    </li>
  );
}

function AssistantTurn({ message, assistantName, revealing, onChoice }) {
  const choices = Array.isArray(message.choices) ? message.choices : [];
  return (
    <li className="ea-turn ea-turn-assistant ea-rise">
      <MiniOrb />
      <div className="ea-turn-body">
        <p className="ea-turn-text">
          <span className="sr-only">{assistantName} said: </span>
          {message.text}
          {revealing ? <span className="ea-caret" aria-hidden="true" /> : null}
        </p>
        {choices.length ? (
          <div className="ea-choices">
            {choices.map((c, i) => (
              <button
                key={c.value || c.label || i}
                type="button"
                className={`ea-choice ${i === 0 ? "ea-choice-primary" : ""}`}
                onClick={() => onChoice && onChoice(c)}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </li>
  );
}

export default function Conversation({
  assistantName = "your assistant",
  ownerName = null,
  live = true,
  compact = false,
  contextLabel = null,
  onActivityChange = null,
}) {
  const { status, messages, sending, streamingText, error, notLive, send } = useConversation({
    limit: compact ? 20 : 60,
  });
  const [draft, setDraft] = useState("");
  // True only while a newly-arrived reply is being revealed. See the header:
  // this is presentation, not streaming.
  const [revealing, setRevealing] = useState(false);
  const endRef = useRef(null);
  const lastId = useRef(null);

  useEffect(() => {
    const end = endRef.current;
    if (end && typeof end.scrollIntoView === "function") {
      end.scrollIntoView({ block: "end" });
    }
  }, [messages.length]);

  // A new assistant turn starts the reveal; it ends on its own.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.who !== "assistant") return undefined;
    if (lastId.current === last.id) return undefined;
    lastId.current = last.id;
    setRevealing(true);
    const t = setTimeout(() => setRevealing(false), 1400);
    return () => clearTimeout(t);
  }, [messages]);

  // The orb lives in the EA home's left panel, outside this component, so the
  // state it should show is reported upward rather than computed twice.
  useEffect(() => {
    if (!onActivityChange) return;
    // Thinking until the first fragment lands, replying from then on — both
    // now read from the stream itself rather than from a timer.
    onActivityChange(
      streamingText ? "replying"
        : sending ? "thinking"
          : revealing ? "replying" : "idle");
  }, [sending, revealing, streamingText, onActivityChange]);

  async function onSubmit(e) {
    e.preventDefault();
    const text = draft;
    setDraft("");
    const res = await send(text);
    if (res && res.restore) setDraft(res.restore);
  }

  if (status === "disabled") {
    return (
      <div className="ea-conv-state">
        <strong>Your assistant is not switched on.</strong>
        <p>The assistant subsystem is off for this deployment, so there is
          nothing to talk to yet.</p>
      </div>
    );
  }
  if (status === "denied") {
    return (
      <div className="ea-conv-state">
        <strong>You are not signed in to your assistant.</strong>
        <p>Sign in again and reopen this page.</p>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="ea-conv-state">
        <strong>The conversation could not be read.</strong>
        <p>{error || "The request failed."}</p>
      </div>
    );
  }

  const dormant = notLive || !live;
  const loading = status === "loading";

  return (
    <div className={`ea-conv ${compact ? "ea-conv-compact" : ""}`}>
      <div className="ea-conv-scroll">
        <div className="ea-conv-column">
          {/* The design's opening, shown only on the full surface — the dock
              is an interruption, not an arrival.

              Rendered BEFORE the read resolves, on purpose: a greeting is not
              conversation, so it does not have to wait for one. Holding it
              back meant the first paint of the assistant's own page was a
              bare "Reading your conversation…" and then a jump. */}
          {!compact ? (
            <header className="ea-greeting">
              <h2>{greetingFor()}{ownerName ? `, ${ownerName}` : ""}.</h2>
              <p>What can I take off your plate?</p>
            </header>
          ) : null}

          {contextLabel ? (
            <p className="ea-conv-context">
              Looking at <strong>{contextLabel}</strong>
            </p>
          ) : null}

          {loading ? (
            <p className="ea-conv-loading">Reading your conversation…</p>
          ) : null}

          <ul className="ea-turns">
            {messages.map((m) => (
              m.who === "owner"
                ? <OwnerTurn key={m.id} message={m} />
                : (
                  <AssistantTurn
                    key={m.id}
                    message={m}
                    assistantName={assistantName}
                    revealing={revealing && m.id === lastId.current}
                  />
                )
            ))}
            {/* The reply as it is being written. A real turn, with the
                caret, so the caret now marks ACTUAL live generation rather
                than a reveal of text already received (#331). It is replaced
                by the server's own stored turn the moment the stream ends. */}
            {streamingText ? (
              <AssistantTurn
                key="streaming"
                message={{ id: "streaming", text: streamingText }}
                assistantName={assistantName}
                revealing
              />
            ) : null}
            <li ref={endRef} aria-hidden="true" />
          </ul>

          {loading ? null : dormant ? (
            <div className="ea-conv-state ea-conv-dormant">
              <strong>Your assistant is dormant.</strong>
              <p>Finish setup and switch it on in Settings before it can reply.</p>
            </div>
          ) : null}
        </div>
      </div>

      {!loading && !dormant ? (
        <div className="ea-composer-dock">
          <form className="ea-composer" onSubmit={onSubmit}>
            <label className="sr-only" htmlFor="ea-composer-input">
              Message {assistantName}
            </label>
            <input
              id="ea-composer-input"
              className="ea-composer-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Ask ${assistantName} anything…`}
              autoComplete="off"
              disabled={sending}
            />
            <button
              type="submit"
              className="ea-composer-send"
              aria-label="Send"
              disabled={sending || !draft.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
          {error ? <p className="ea-conv-error" role="alert">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
